/**
 * Par-value ownership shares.
 *
 * Stored and sent as `parValueBps` — basis points of 100%, so 1.37% = 137 — but NOT
 * an integer: associations carry shares to 3 decimals (Hunter's Crossing 1.366%) and
 * some to 6 (1.123456%), which is 136.6 and 112.3456 bps. Integer bps silently rounded
 * those to 1.37% and over-billed (2026-10-30). The field and column keep their name so
 * older app builds still read a usable value; SQLite stores a fractional value in the
 * INTEGER-affinity column as REAL.
 */

/** Decimals of a PERCENT a share may carry (1.123456%). */
export const PAR_SHARE_DECIMALS = 6;

/** Steps per basis point at that precision: 6 decimals of a percent = 4 decimals of a bp. */
export const MICRO_PCT_PER_BPS = 10_000;

/** A typed percent (1.366) → bps (136.6), cut to six decimals of a percent. */
export const percentToParBps = (pct: number): number => Math.round(pct * 1_000_000) / MICRO_PCT_PER_BPS;

/**
 * Parse an ownership percentage into basis points (4.23% → 423, 1.366% → 136.6).
 *
 * Accepts a bare number or one with a trailing `%` — exports from other
 * management systems disagree on which they emit, and a silently-dropped `%`
 * would be indistinguishable from a valid share. Returns null for blank or
 * unparseable input, and for anything outside 0 < pct <= 100.
 */
export const parsePercentToBps = (raw: string | number | null | undefined): number | null => {
	if (raw == null) return null;
	const trimmed = String(raw).trim().replace(/%$/, '').trim();
	if (trimmed === '') return null;
	const pct = Number(trimmed.replace(/,/g, ''));
	if (!isFinite(pct) || pct <= 0 || pct > 100) return null;
	return percentToParBps(pct);
};

/**
 * A share as an exact integer count of millionths of a percent (136.6 bps → 1,366,000).
 * Dues math runs on this, never on the float, so a line that lands exactly on 50 cents
 * rounds the same way every time.
 */
export const parBpsToMicroPct = (bps: number): number => Math.round(bps * MICRO_PCT_PER_BPS);

/** True when `bps` is a valid share: 0–100%, at most six decimals of a percent. */
export const isValidParBps = (bps: number): boolean =>
	Number.isFinite(bps) && bps >= 0 && bps <= 10_000 && Math.abs(bps * MICRO_PCT_PER_BPS - parBpsToMicroPct(bps)) < 1e-6;

/** `1.37%`, `1.366%`, `1.123456%` — at least two decimals, at most six, no trailing zeros past two. */
export const formatParPercent = (bps: number): string => {
	const fixed = (bps / 100).toFixed(PAR_SHARE_DECIMALS).replace(/0+$/, '');
	const [whole, frac = ''] = fixed.split('.');
	return `${whole}.${frac.padEnd(2, '0')}%`;
};

/** The percent to pre-fill an input with: `1.366`, `1.37` — no forced trailing zeros past two. */
export const parBpsToPercentInput = (bps: number): string => formatParPercent(bps).slice(0, -1);

/**
 * True when `incoming` is what an app build from BEFORE fractional shares sends back for a
 * `stored` fractional share it merely displayed: it showed `(bps / 100).toFixed(2)` and
 * submitted `Math.round(pct * 100)` on every account save, so 136.6 came back as 137 even
 * when the user only changed a phone number (Codex P1, #702). The write path keeps the stored
 * share instead — a lossy echo is not an edit. A current build sends the exact value, and is
 * the way to deliberately round a share.
 */
export const isLegacyRoundedParEcho = (stored: number | null | undefined, incoming: number | null | undefined): boolean => {
	if (stored == null || incoming == null || Number.isInteger(stored) || !Number.isInteger(incoming)) return false;
	const legacyEcho = Math.round(Number((stored / 100).toFixed(2)) * 100);
	return incoming === legacyEcho;
};
